import { useEffect, useCallback, useRef } from 'react';
import type { PlayerSide, KeyMapping } from '../types/dualGame';

interface UseKeyboardInputOptions {
  isPlaying: boolean;
  keyMapping?: KeyMapping;
  onKeyDown: (player: PlayerSide, direction: 'up' | 'down' | 'left' | 'right') => void;
  onPause?: () => void;
  preventDefault?: boolean;
}

export const useKeyboardInput = ({
  isPlaying,
  keyMapping = {},
  onKeyDown,
  onPause,
  preventDefault = true,
}: UseKeyboardInputOptions) => {
  const callbackRef = useRef(onKeyDown);
  const mappingRef = useRef(keyMapping);
  const pauseRef = useRef(onPause);

  useEffect(() => { callbackRef.current = onKeyDown; }, [onKeyDown]);
  useEffect(() => { mappingRef.current = keyMapping; }, [keyMapping]);
  useEffect(() => { pauseRef.current = onPause; }, [onPause]);

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const key = event.key;

    if (key === 'p' || key === 'P') {
      pauseRef.current?.();
      return;
    }

    if (!isPlaying) return;

    const action = mappingRef.current[key];
    if (!action) return;

    if (preventDefault) {
      event.preventDefault();
    }

    callbackRef.current(action.player, action.direction);
  }, [isPlaying, preventDefault]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const triggerMove = useCallback((player: PlayerSide, direction: 'up' | 'down' | 'left' | 'right') => {
    if (!isPlaying) return;
    onKeyDown(player, direction);
  }, [isPlaying, onKeyDown]);

  return { triggerMove };
};

export default useKeyboardInput;
